import type { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import { parseDateOnly } from '@/lib/utils/date-only'
import { odooUrl } from '@/lib/utils/odoo-url'
import type { ShipConfirmationTracker } from '../api/types'
import { SHIP_STATUS_LABELS, SHIP_STATUS_VARIANT, shipStatusOf } from './ship-confirmation-stage-meta'

export interface BuildLineColumnsOptions {
  onConfirm: (t: ShipConfirmationTracker) => void
  onLogEstimate: (t: ShipConfirmationTracker) => void
  onUndo: (t: ShipConfirmationTracker) => void
  /** "Split this line into partial shipments" — opens `SplitLineModal`.
   *  Only offered on a non-split `awaiting_confirmation` row. */
  onSplit: (t: ShipConfirmationTracker) => void
  /** Row ids (string `tracker.id`) currently expanded to show their
   *  split breakdown — drives both the toggle affordance and whether
   *  `DataTable`'s `renderExpandedRow` renders for that row. */
  expandedRowIds: ReadonlySet<string>
  onToggleExpand: (trackerId: string) => void
  /** Show the combined "PO:LN" + Odoo-link column. Default true (the flat
   *  table view, where rows span many POs). Set false inside a single-PO
   *  context (the detail drawer) where the PO is already named in the
   *  header — shows a plain line number instead. */
  showPoColumn?: boolean
  /** Show the vendor column. Default true; set false inside a single-PO
   *  context where the vendor is already named in the header. */
  showVendorColumn?: boolean
}

/**
 * Per-line column set shared by the flat table view
 * (`PoShipDateConfirmationPage`) and the per-PO detail drawer
 * (`PoShipmentDetailDrawer`) — one implementation of the
 * Confirm/Log-estimate/Revise/Undo action logic instead of two.
 */
export function buildLineColumns({
  onConfirm,
  onLogEstimate,
  onUndo,
  onSplit,
  expandedRowIds,
  onToggleExpand,
  showPoColumn = true,
  showVendorColumn = true,
}: BuildLineColumnsOptions): ColumnDef<ShipConfirmationTracker>[] {
  const columns: ColumnDef<ShipConfirmationTracker>[] = []

  columns.push(
    showPoColumn
      ? {
          id: 'po_ln',
          header: 'PO:LN',
          size: 110,
          cell: ({ row }) => {
            const { odoo_po_id, odoo_po_name, line_number } = row.original
            const label = line_number ? `${odoo_po_name}:${line_number}` : odoo_po_name
            return (
              <a
                href={odooUrl('purchase.order', odoo_po_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-accent-text hover:underline"
              >
                {label}
              </a>
            )
          },
        }
      : {
          id: 'line_number',
          header: 'Line',
          size: 70,
          cell: ({ row }) =>
            row.original.line_number ?? <span className="text-tertiary">—</span>,
        },
  )

  columns.push({
    accessorKey: 'product_name',
    header: 'Product',
    size: 180,
    cell: ({ getValue }) => getValue<string | null>() ?? <span className="text-tertiary">—</span>,
  })

  if (showVendorColumn) {
    columns.push({
      accessorKey: 'vendor_name',
      header: 'Vendor',
      size: 180,
      cell: ({ getValue }) => getValue<string | null>() ?? <span className="text-tertiary">—</span>,
    })
  }

  columns.push(
    {
      id: 'qty',
      header: 'Qty',
      size: 130,
      cell: ({ row }) => {
        const { id, product_qty, open_split_count } = row.original
        if (open_split_count > 0) {
          const isExpanded = expandedRowIds.has(String(id))
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(String(id))
              }}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} splits for this line`}
              className="flex items-center gap-1 text-secondary hover:text-primary"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <Chip size="xs" variant="info">
                Split ×{open_split_count}
              </Chip>
            </button>
          )
        }
        return product_qty ?? <span className="text-tertiary">—</span>
      },
    },
    {
      accessorKey: 'stage',
      header: 'Stage',
      size: 150,
      cell: ({ row }) => {
        const status = shipStatusOf(row.original)
        return (
          <Chip size="xs" variant={SHIP_STATUS_VARIANT[status]}>
            {SHIP_STATUS_LABELS[status]}
          </Chip>
        )
      },
    },
    {
      id: 'ship_date',
      header: 'Ship Date',
      size: 160,
      cell: ({ row }) => {
        const { stage, confirmed_ship_date, latest_estimated_date } = row.original
        if (stage === 'confirmed' || stage === 'received') {
          return formatDate(confirmed_ship_date)
        }
        const count = row.original.estimate_count ?? 0
        const date = latest_estimated_date ? (
          <span className="italic text-tertiary">Est. {formatDate(latest_estimated_date)}</span>
        ) : (
          <span className="text-tertiary">No date on file</span>
        )
        if (count === 0) return date
        // Logged estimates: the toggle opens the estimate history in the
        // row's expanded panel (shared with the split breakdown).
        const id = String(row.original.id)
        const isExpanded = expandedRowIds.has(id)
        const slip = row.original.slip_days
        return (
          <div className="flex flex-col items-start gap-0.5">
            {date}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(id)
              }}
              aria-expanded={isExpanded}
              className="flex items-center gap-1 text-xs text-secondary hover:text-primary"
            >
              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {count} logged{slip ? ` · ${slip > 0 ? '+' : ''}${slip}d` : ''}
            </button>
          </div>
        )
      },
    },
    {
      accessorKey: 'confirmation_source',
      header: 'Source',
      size: 150,
      cell: ({ getValue }) => getValue<string | null>() ?? <span className="text-tertiary">—</span>,
    },
    {
      id: 'days_open',
      header: 'Days Open',
      size: 100,
      cell: ({ row }) => daysOpen(row.original.created_at),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 160,
      cell: ({ getValue }) => formatDateTime(getValue<string>()),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 260,
      cell: ({ row }) => {
        const tracker = row.original
        if (tracker.open_split_count > 0) {
          const isExpanded = expandedRowIds.has(String(tracker.id))
          return (
            <Button variant="ghost" size="sm" onClick={() => onToggleExpand(String(tracker.id))}>
              {isExpanded ? 'Hide splits' : 'View splits'}
            </Button>
          )
        }
        if (tracker.stage === 'awaiting_confirmation') {
          return (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => onConfirm(tracker)}>
                Confirm
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onLogEstimate(tracker)}>
                Log estimate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onSplit(tracker)}>
                Split
              </Button>
            </div>
          )
        }
        if (tracker.stage === 'confirmed') {
          return (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => onConfirm(tracker)}>
                Revise
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onUndo(tracker)}>
                Undo
              </Button>
            </div>
          )
        }
        return <span className="text-tertiary">—</span>
      },
    },
  )

  return columns
}

function formatDate(iso: string | null): React.ReactNode {
  // confirmed_ship_date / latest_estimated_date are date-only ("YYYY-MM-DD")
  // — parseDateOnly avoids the classic new Date(dateOnlyString) UTC-midnight
  // shift that displays the prior calendar day in any US timezone.
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return parseDateOnly(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatDateTime(iso: string | null): React.ReactNode {
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch {
    return iso
  }
}

function daysOpen(iso: string): number {
  const created = new Date(iso).getTime()
  if (Number.isNaN(created)) return 0
  return Math.max(0, Math.floor((Date.now() - created) / 86_400_000))
}
